import {
  createAsyncThunk,
  EntityState,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit';
import { FORM_ERROR } from 'final-form';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { showNotification } from '@vk-hr-tek/core/notifications';
import { DownloadService } from '@vk-hr-tek/core/download';

import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import {
  UploadTemplateResponse,
  TemplatesService,
  UpdateTemplateFileParams,
  UploadTemplateFileParams,
  UploadTemplateRequest,
} from '@app/gen/templates';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';

import { SettingsTemplateUploadTransformMapper } from '../../../mappers';
import {
  SettingsState,
  resetUploadState,
  SettingsWithRootState,
} from '../../settings.state';
import { SettingsTemplatesRouter, TemplateInformation } from '../../../types';

export const downloadTemplateExample = createAsyncThunk<
  void,
  void,
  ThunkExtra<SettingsWithRootState>
>(
  'templates/downloadTemplateExample',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const templatesService = inject(TemplatesService);

      await templatesService
        .getTemplateExampleFile()
        .then(async ({ file, filename }) => {
          await inject(DownloadService).doDownload(
            file,
            filename || 'example.docx',
          );
        });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const uploadTemplate = createAsyncThunk<
  UploadTemplateResponse,
  ThunkValuesAndActions<
    UploadTemplateFileParams & UploadTemplateRequest & { company_id: string }
  >,
  ThunkExtra<SettingsWithRootState>
>(
  'templates/uploadTemplate',
  async ({ values, actions }, { rejectWithValue, extra: { inject } }) => {
    try {
      const router = inject<SettingsTemplatesRouter>(SettingsTemplatesRouter);
      const templatesService = inject(TemplatesService);

      const result = await templatesService.uploadTemplateFile({
        params: { template_id: values.template_id },
        body: { file: values.file as File },
      });

      setTimeout(() => {
        router.goToDetailPreviewUpload({
          id: values.template_id,
          companyId: values.company_id,
        });
      }, 0);

      return result;
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getTemplateInformation = createAsyncThunk<
  TemplateInformation,
  { template_id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'templates/getTemplateInformation',
  async ({ template_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const templatesService = inject(TemplatesService);
      const result = await templatesService.getTemplate({
        params: { template_id },
      });

      const transformService = inject(SettingsTemplateUploadTransformMapper);
      return transformService.transform(result);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const acceptTemplate = createAsyncThunk<
  void,
  ThunkValuesAndActions<UpdateTemplateFileParams & { company_id: string }>,
  ThunkExtra<SettingsWithRootState>
>(
  'templates/acceptTemplate',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const router = inject<SettingsTemplatesRouter>(SettingsTemplatesRouter);
      const templatesService = inject(TemplatesService);
      await templatesService.updateTemplateFile({
        params: { template_id: values.template_id, file_id: values.file_id },
      });

      if (values.company_id) {
        router.goToList(
          `?companyId=${values.company_id}#${values.template_id}`,
        );
      } else {
        router.goToList();
      }

      dispatch(showNotification('Новый шаблон успешно сохранен!'));
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsUploadTemplateReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getTemplateInformation.pending, (state) => {
    state.templateUpload.status = 'loading';
  });
  builder.addCase(getTemplateInformation.fulfilled, (state, { payload }) => {
    resetUploadState(state);
    state.templateUpload.status = 'complete';
    state.templateUpload.templateInformation = payload;
  });
  builder.addCase(
    getTemplateInformation.rejected,
    (state, { payload, error }) => {
      state.templateUpload.status = 'failed';
      state.templateUpload.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
  builder.addCase(uploadTemplate.fulfilled, (state, { payload }) => {
    state.templateUpload.uploadResponsePlaceholders = payload.placeholders;
    state.templateUpload.newFileId = payload.file_id;
    state.templateUpload.uploadAlertMessage = payload.alert || null;
  });
  builder.addCase(acceptTemplate.fulfilled, (state) => {
    resetUploadState(state);
  });
};
